<template>
  <main :class="{ loading }">
    <Header>
      <router-link
        id="back-to-pages"
        :to="{ name: 'pagelist' }"
        class="header__action"
      >
        <IconArrow direction="left" />
        Voltar para páginas
      </router-link>
      <h2 class="hotel-title">
        {{ siteHostname }}
      </h2>
    </Header>
    <div class="pages">
      <div class="pages__management">
        <div class="pages__management__header">
          <h1>Páginas excluídas</h1>
        </div>
        <div
          v-if="deletedPages.length === 0"
          class="pages__empty-state text-center"
        >
          <p>Lixeira vazia 😸</p>
        </div>
        <CustomTable
          v-show="deletedPages.length > 0 && !loading"
          id="trash-page-list"
          ref="customTable"
          class="pages__table"
          :cols="tableHeaders"
          :list="listForTable"
        >
          <template #title="{ row: page }">
            <div
              class="column-title"
              :class="{
                'translation-page': page.language !== 'pt',
              }"
            >
              <IconLanguage
                size="xs"
                :language="page.language"
              />
              <span>{{ page.title }} </span>
            </div>
          </template>
          <template #actions="{ row: page }">
            <div>
              <div class="column-actions">
                <button
                  class="column-actions__cta"
                  :title="restoreTitle(page)"
                  :disabled="disableRestore(page) || isViewerUser"
                  @click="restorePage(page)"
                >
                  Restaurar
                </button>
                <button
                  class="column-actions__cta trash-delete"
                  title="Excluir permanentemente a página"
                  :disabled="isViewerUser"
                  @click.stop="pageBeingPermaDeleted = page"
                >
                  Excluir permanentemente
                </button>
              </div>
            </div>
          </template>
        </CustomTable>
      </div>

      <div
        v-show="isRemoveModalOpen"
        class="pages__delete-page-modal"
        @click.self="pageBeingPermaDeleted = null"
      >
        <div class="pages__delete-page-modal__content">
          <div class="pages__delete-page-modal__content__header">
            <label
              class="btn-close"
              @click="pageBeingPermaDeleted = null"
            >
              <IconClose />
            </label>
            <IconDelete
              class="text-danger"
              size="lg"
            />
            <h2>Deseja excluir permanentemente a página selecionada?</h2>
          </div>

          <div class="pages__delete-page-modal__content__text">
            <p>
              Após excluir permanentemente, não será possível restaurar a
              <br />
              página de nenhuma forma.
            </p>
          </div>

          <div class="pages__delete-page-modal__content__buttons">
            <button
              class="btn btn-neutral"
              type="button"
              @click="pageBeingPermaDeleted = null"
            >
              Voltar
            </button>
            <button
              class="btn btn-danger"
              type="submit"
              @click="deletePage()"
            >
              Excluir permanentemente
            </button>
          </div>
        </div>
      </div>
    </div>
    <Footer />
  </main>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

import Header from '@/components/Header.vue';
import CustomTable from '@/components/CustomTable.vue';
import IconLanguage from '@/components/icons/IconLanguage.vue';
import IconClose from '@/components/icons/IconClose.vue';
import IconDelete from '@/components/icons/IconDelete.vue';
import IconArrow from '@/components/icons/IconArrow.vue';
import Footer from '@/components/Footer.vue';

export default {
  name: 'TrashPage',
  components: {
    Header,
    Footer,
    CustomTable,
    IconClose,
    IconDelete,
    IconLanguage,
    IconArrow,
  },
  data() {
    return {
      pageBeingPermaDeleted: null,
    };
  },
  computed: {
    isRemoveModalOpen() {
      return this.pageBeingPermaDeleted !== null;
    },
    listForTable() {
      return this.deletedPages.map((parentPage) => {
        const isHome = parentPage.urlPath === '_';
        const children = parentPage.translations.map((childrenPage) => ({
          ...childrenPage,
          urlPath: `/${childrenPage.language}/${childrenPage.urlPath}`,
        }));

        return {
          ...parentPage,
          urlPath: isHome ? '/' : `/${parentPage.urlPath}`,
          children,
        };
      });
    },
    tableHeaders() {
      return [
        {
          name: 'title',
          label: 'Nome',
        },
        {
          name: 'urlPath',
          label: 'URL',
        },
        {
          name: 'deleted',
          label: 'Data de exclusão',
          maxWidth: '160px',
          type: 'date',
        },
        {
          name: 'actions',
          label: 'Ações',
          maxWidth: '300px',
          isSortable: false,
        },
      ];
    },
    ...mapState(['deletedPages', 'loading', 'pages']),
    ...mapGetters(['siteHostname', 'isViewerUser']),
  },
  methods: {
    disableRestore(page) {
      return page.language !== 'pt';
    },
    restoreTitle(page) {
      if (page.language !== 'pt') {
        return 'Somente páginas em português podem ser restauradas';
      }
      return 'Restaurar página';
    },
    restorePage(page) {
      this.$store.dispatch('restorePage', page).then(() => {
        const restoredPageId = page.id;
        const restoredPage = this.pages[restoredPageId];

        this.goToEditor({
          urlPath: restoredPage.urlPath,
          language: restoredPage.language,
        });
      });
    },
    deletePage() {
      this.$store
        .dispatch('deletePage', this.pageBeingPermaDeleted)
        .then(() => {
          this.pageBeingPermaDeleted = null;
        });
    },
    goToEditor(page) {
      this.$router.push({ name: 'editor', params: page });
    },
  },
};
</script>

<style scoped lang="scss">
main {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: #f2f2f2;
  margin: 0;
  padding-bottom: 45px; // footer height

  header {
    position: unset;
    width: unset;
  }

  &.loading {
    opacity: 0.75;
    cursor: progress;

    > * {
      pointer-events: none;
    }
  }
}

.pages {
  width: 100%;
  max-width: 1600px;
  flex-grow: 1;
  margin: 0 auto;
  padding: 32px 16px 24px;

  &__management {
    padding: 32px;
    background-color: $lb-white;

    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;

      h1 {
        font-size: $size-bigger;
        font-weight: $lb-medium;
      }
    }
  }

  &__empty-state {
    height: 350px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: $size-normal;
  }

  &__table {
    margin-top: 20px;

    ::v-deep {
      [data-identifier='actions'],
      [data-identifier='deleted'] {
        justify-content: center;
      }
    }

    .column-title {
      display: flex;
      align-items: center;
      width: 100%;

      .icon {
        margin-right: 12px;
      }

      .icon {
        flex-shrink: 0;
      }
    }

    .column-actions {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;

      > * + * {
        margin-left: 8px;
      }

      &__cta {
        color: $crm-blue-primary;
        font-weight: $lb-medium;
        padding: 6px;

        &:hover {
          text-decoration: underline;
        }

        &[disabled] {
          text-decoration: none;
          color: #7f7f7f;
          cursor: not-allowed;
          opacity: 0.7;

          &:hover {
            text-decoration: none;
          }
        }

        &.trash-delete {
          color: $crm-red-primary;
        }
      }
    }
  }

  &__delete-page-modal {
    position: fixed;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    background-color: rgba($lb-dark-gray, 0.4);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2;

    &__content {
      width: 545px;
      display: flex;
      padding: 40px;
      flex-direction: column;
      background-color: $lb-white;
      align-items: center;

      &__header {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        flex-direction: column;
        position: relative;

        h2 {
          font-size: $size-bigger;
          font-weight: $lb-medium;
          line-height: 35px;
          text-align: center;
          letter-spacing: -1px;
          color: $gray-900;
          margin: 40px 0 20px 0;
        }

        .btn-close {
          position: absolute;
          top: 0;
          right: 0;
          cursor: pointer;
        }
      }

      &__text {
        display: flex;
        flex-direction: column;
        justify-content: center;
        text-align: center;

        p {
          font-size: $size-normal;
          line-height: 26px;
          color: $gray-900;
          margin-bottom: 20px;
        }
      }

      &__buttons {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;

        button + button {
          margin-left: 12px;
        }
      }
    }
  }
}

footer {
  position: fixed;
  bottom: 0;
  left: 0;
}
</style>
